import type { FC } from 'react';
import React from 'react';
import { useTranslation, createFeature } from '@edreamsodigeo/frontend-core';

import type { BaggageConditionTextProps, BaggageConditionType } from './types';
import { RAILS_BAGGAGE_INFORMATION } from '@/versionedComponents';
import { getDisplayedCarriers } from '@/components/FlightSummary/Carrier/utils';
import { useGetBagsNamingCleanup } from './utils/useGetBagsNamingCleanup';
import { getBaggageKey } from './utils/getBaggageKey';

const useGetBaggageConditionKey = (
  baggageCondition: BaggageConditionType,
  hasCabinBag?: boolean
) => {
  const isBagsNamingCleanupEnabled = useGetBagsNamingCleanup();
  const cabinBagIncludedKey = isBagsNamingCleanupEnabled
    ? 'cabinbags.option.cabin_bag.alias.included'
    : 'cabinbags.option.cabin_bag.included';
  return hasCabinBag
    ? cabinBagIncludedKey
    : getBaggageKey(baggageCondition, isBagsNamingCleanupEnabled);
};

const BaggageConditionTextTrainVariation: FC<BaggageConditionTextProps> = ({
  baggageCondition,
  hasCabinBag,
  section,
  isShort,
}) => {
  const { t } = useTranslation();
  const carrierIds = getDisplayedCarriers(
    section?.carrier ? [section.carrier] : [],
    section?.commercialBrand ? [section.commercialBrand] : [],
    true
  );
  const size = isShort ? 'short' : 'long';
  const displayText = carrierIds[0]
    ? t(`railsBaggage.description.${size}.${carrierIds[0].id}`)?.trim() || ''
    : '';
  const key = useGetBaggageConditionKey(baggageCondition, hasCabinBag);

  const finalText = displayText || t(key);
  return <>{finalText}</>;
};

const BaggageConditionTextLegacy: FC<BaggageConditionTextProps> = ({
  baggageCondition,
  hasCabinBag,
}) => {
  const { t } = useTranslation();
  const key = useGetBaggageConditionKey(baggageCondition, hasCabinBag);
  return <>{t(key)}</>;
};

export const BaggageConditionText = createFeature({
  name: RAILS_BAGGAGE_INFORMATION,
  variations: [BaggageConditionTextLegacy, BaggageConditionTextTrainVariation],
  version: 1,
});
